'use client';

import { CONTENT_EVENTS } from '@repo/analytics';
import { useSession } from '@repo/auth';
import { getWatchStatusBatchV1Api, rateMovieV1Api } from '@repo/sdk';
import { toast } from '@repo/ui';
import { CollectionCard } from '@repo/web-chrome/components/explore/collection-card';
import { trackEvent } from '@repo/web-chrome/lib/analytics';
import type React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { CollectionWatchToggle } from './collection-watch-toggle';

export interface ICollectionChildGridItem {
  id: number;
  slug: string;
  title: string;
  posterUrl: string | null;
  titleInPoster: number;
  publishedChallengesCount: number;
  countLabel: string;
}

interface ICollectionChildrenGridProps {
  items: ICollectionChildGridItem[];
  markWatchedLabel: string;
  markUnwatchedLabel: string;
  saveErrorMessage: string;
}

export function CollectionChildrenGrid({
  items,
  markWatchedLabel,
  markUnwatchedLabel,
  saveErrorMessage,
}: ICollectionChildrenGridProps): React.JSX.Element {
  const { data: session } = useSession();
  const signedIn = Boolean(session?.user);
  const [watched, setWatched] = useState<Set<number>>(new Set());
  const [busy, setBusy] = useState<Set<number>>(new Set());

  useEffect(() => {
    if (!signedIn || items.length === 0) {
      return;
    }
    let active = true;
    getWatchStatusBatchV1Api({ collectionIds: items.map((item) => item.id) })
      .then((res) => {
        if (!active) {
          return;
        }
        const ids = (res.data.data?.statuses ?? [])
          .filter((status) => status.watched)
          .map((status) => status.collectionId);
        setWatched(new Set(ids));
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [signedIn, items]);

  const toggle = useCallback(
    async (id: number): Promise<void> => {
      const next = !watched.has(id);
      setBusy((prev) => new Set(prev).add(id));
      setWatched((prev) => {
        const set = new Set(prev);
        if (next) {
          set.add(id);
        } else {
          set.delete(id);
        }
        return set;
      });
      trackEvent(next ? CONTENT_EVENTS.movieWatched : CONTENT_EVENTS.movieUnwatched, {
        collectionId: id,
      });
      try {
        await rateMovieV1Api({ collectionId: id, watched: next });
      } catch {
        setWatched((prev) => {
          const set = new Set(prev);
          if (next) {
            set.delete(id);
          } else {
            set.add(id);
          }
          return set;
        });
        toast.error(saveErrorMessage);
      } finally {
        setBusy((prev) => {
          const set = new Set(prev);
          set.delete(id);
          return set;
        });
      }
    },
    [watched, saveErrorMessage],
  );

  return (
    <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
      {items.map((child) => (
        <div key={child.id} className="relative">
          <CollectionCard
            slug={child.slug}
            title={child.title}
            posterUrl={child.posterUrl}
            titleInPoster={child.titleInPoster}
            challengeCount={child.publishedChallengesCount}
            countLabel={child.countLabel}
          />
          {signedIn ? (
            <div className="absolute end-2 bottom-2">
              <CollectionWatchToggle
                watched={watched.has(child.id)}
                pending={busy.has(child.id)}
                markWatchedLabel={markWatchedLabel}
                markUnwatchedLabel={markUnwatchedLabel}
                onToggle={() => toggle(child.id)}
              />
            </div>
          ) : null}
        </div>
      ))}
    </div>
  );
}
